import { KeyboardEvent, useContext, useEffect, useMemo, useState } from 'react';
import { useJsApiLoader } from '@react-google-maps/api';
import Slider from '@mui/material/Slider';
import OutlinedInput from '@mui/material/OutlinedInput';
import FormControl from '@mui/material/FormControl';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import debounce from 'lodash/debounce';
import {
  BodyMedium,
  BodyLargeEmphasized,
  NavBarBackButton,
  ButtonPrimaryMedium,
  ButtonFlatMedium,
  useSnackbar,
  BodySmall,
  BodySmallMidEmphasis,
} from '@/design-system';
import colors from '@/theme/colors';
import { APP_ROUTES, DEFAULT_ERROR_MESSAGE } from '@/config/constants';
import { useNavigate } from 'react-router-dom';
import styles from './Community.styles';

import { Map } from './components/Map';
import UserPreferencesContext from '@/context/UserPreferencesContext';
import { FindLocalAreaResponse, Location, SchoolResult } from '@/graphql/types';
import { LiveFeedback, PeachjarLoader, Translate } from '@/components';
import { useLiveFeedback, useNavigationPreviousPage } from '@/hooks';
import schoolInYourDistrictIcon from '@/assets/school-in-your-district.svg';
import schoolOutsideYourDistrictIcon from '@/assets/school-outside-your-district.svg';
import { useQuery, useReactiveVar } from '@apollo/client';
import { districtVar } from '@/graphql/state';
import { FIND_LOCAL_AREA } from '@/graphql/queries';
import { NavigationContext } from '@/context/NavigationContext';
import { Autocomplete } from './components/Autocomplete';

const MIN_DISTANCE_SLIDER_MILES = 5;
const MAX_DISTANCE_SLIDER_MILES = 50;
const MIN_DISTANCE_SLIDER_KILOMETERS = 8;
const MAX_DISTANCE_SLIDER_KILOMETERS = 80;

export const Community: React.FC = () => {
  const { loading, community, updateUserPreferences } = useContext(UserPreferencesContext);
  const { previousPage } = useContext(NavigationContext);
  const navigatePreviousPage = useNavigationPreviousPage();
  const [address, setAddress] = useState<string>('');
  const [location, setLocation] = useState<Location>({
    lat: 0,
    lng: 0,
  });
  const [metricType, setMetricType] = useState<string>('mi');
  const [distance, setDistance] = useState<number>(0);
  const [debouncedDistance, setDebouncedDistance] = useState<number>(distance);
  const [localArea, setLocalArea] = useState<string>('');
  const [feedbackMessage, setFeedbackMessage] = useLiveFeedback({ delay: 2000 });
  const [sliderConfig, setSliderConfig] = useState<{
    min: number;
    max: number;
  }>({ min: MIN_DISTANCE_SLIDER_MILES, max: MAX_DISTANCE_SLIDER_MILES });
  const [districtSchools, setDistrictSchools] = useState<SchoolResult[]>([]);
  const district = useReactiveVar(districtVar);
  const flyearboardRoutes: string[] = [APP_ROUTES.exploreAllFlyers, APP_ROUTES.exploreNewFlyers];

  const debouncedDistanceChange = useMemo(
    () =>
      debounce((newDistance: number) => {
        setDebouncedDistance(newDistance);
      }, 500),
    []
  );

  const { data, loading: localAreaLoading } = useQuery<FindLocalAreaResponse>(FIND_LOCAL_AREA, {
    variables: {
      input: {
        location: { lat: location.lat, lng: location.lng },
        districtId: district?.districtId,
        distance: debouncedDistance,
        metricType,
      },
    },
    skip: (!location.lat || !location.lng) && !district?.districtId,
    fetchPolicy: 'network-only',
  });

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: `${process.env.REACT_APP_GOOGLE_API_KEY}`,
    libraries: ['places'],
  });
  const navigate = useNavigate();
  const showToast = useSnackbar();
  const isAddressInvalid = address.length === 0;

  useEffect(() => {
    loadCommunityInfo();
  }, [community]);

  useEffect(() => {
    if (data?.findLocalArea) {
      const { localArea, address: localAreaAddress, schools = [] } = data.findLocalArea;
      setLocalArea(localArea);
      setAddress(localAreaAddress);
      setDistrictSchools(schools);
    }
  }, [data]);

  const loadCommunityInfo = async () => {
    if (!community) return;

    const { distance, localArea, metricType, address, location } = community;
    const { lat, lng } = location;
    setLocation({ lat, lng });
    setDistance(distance);
    setLocalArea(localArea);
    setMetricType(metricType);
    setAddress(address);
  };

  const onClickButtonSave = async () => {
    const result = await updateUserPreferences({
      community: {
        distance,
        metricType,
        address,
        localArea,
        location,
      },
    });
    if (result) {
      const successMessage = 'Community saved.';
      const navigateTo = flyearboardRoutes.some(route => previousPage.includes(route))
        ? previousPage
        : APP_ROUTES.preference;
      navigate(navigateTo);
      setFeedbackMessage(`${successMessage} You will be redirected to the preference page`);
      showToast({ message: successMessage });
    } else {
      showToast({ message: DEFAULT_ERROR_MESSAGE });
    }
  };

  const onClickButtonCancel = () => {
    loadCommunityInfo();
    setFeedbackMessage('Location radius reset');
  };

  const onChangeMetricType = (event: SelectChangeEvent) => {
    const newMetricType = event.target.value;
    setMetricType(newMetricType);
    convertDistance(newMetricType);
  };

  const convertDistance = (newMetricType: string) => {
    if (newMetricType === 'km') {
      updateDistance(Math.round(distance * 1.6));
      setSliderConfig({
        min: MIN_DISTANCE_SLIDER_KILOMETERS,
        max: MAX_DISTANCE_SLIDER_KILOMETERS,
      });
    } else {
      updateDistance(Math.round(distance / 1.6));
      setSliderConfig({
        min: MIN_DISTANCE_SLIDER_MILES,
        max: MAX_DISTANCE_SLIDER_MILES,
      });
    }
  };

  const handleDistanceChange = (event: Event, newValue: number | number[]) => {
    updateDistance(newValue as number);
  };

  const updateDistance = (newDistance: number) => {
    setDistance(newDistance);
    debouncedDistanceChange(newDistance);
  };

  const distanceKeyDown = (event: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
    }
  };

  const handleDistanceInputChange = ({
    target: { value },
  }: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    const regex = /^[0-9\b]+$/;
    if (value === '' || regex.test(value)) {
      if (
        (value !== '' && metricType === 'km' && Number(value) > 80) ||
        (metricType === 'mi' && Number(value) > 50)
      )
        return;
      setDistance(Number(value));
    }
  };

  const handleDistanceBlur = () => {
    if (distance < 5) {
      setDistance(5);
    }
  };

  const onChangeMapPosition = (newLocation: Location) => {
    setLocation(newLocation);
  };

  return (
    <>
      <NavBarBackButton trackId="communityPage">Community</NavBarBackButton>
      <PeachjarLoader loading={loading} />
      <main className={styles.container}>
        <LiveFeedback message={feedbackMessage} ariaLive="assertive" />
        <div className={styles.description}>
          <BodyMedium color={colors.slate}>
            You receive community flyers distributed through schools within your district, visible
            on the map. Additionally, you'll receive flyers from schools outside your district that
            fall within your defined community circle. <br />
            <br />
            Start by setting the location of your community using the map below:
          </BodyMedium>
        </div>
        {isLoaded && (
          <Autocomplete
            updateMapPosition={onChangeMapPosition}
            textSearch={address}
            setTextSearch={setAddress}
            setLocalArea={setLocalArea}
            hasError={isAddressInvalid}
          />
        )}
        <div className={styles.mapContainer}>
          <BodySmall color={colors.slate} as="h2">
            <Translate>Your Local Area:</Translate>&nbsp;
          </BodySmall>
          <BodySmall data-testid="localArea" as="h2">
            "{localArea}"
          </BodySmall>
          {isLoaded && (
            <Map
              distance={distance}
              metricType={metricType}
              latitude={location.lat}
              longitude={location.lng}
              updateMapPosition={onChangeMapPosition}
              districtSchools={districtSchools}
            />
          )}
          <div className={styles.legendContainer}>
            <div className={styles.legend}>
              <img
                src={schoolInYourDistrictIcon}
                width="24px"
                height="24px"
                alt="School In your district pin"
              />
              <BodySmallMidEmphasis>Schools in your district</BodySmallMidEmphasis>
            </div>
            <div className={styles.legend}>
              <img
                src={schoolOutsideYourDistrictIcon}
                width="24px"
                height="24px"
                alt="School outside your district pin"
              />
              <BodySmallMidEmphasis>Schools outside of your district</BodySmallMidEmphasis>
            </div>
          </div>
          <fieldset
            className={styles.distanceFieldset}
            aria-labelledby="distance-legend"
          >
            <legend className={styles.distanceLegend}>
              <BodyLargeEmphasized as="h3" id="distance-legend">
                <Translate>Distance:</Translate>
              </BodyLargeEmphasized>
            </legend>
            <div className={styles.distanceContainer}>
              <span id="distance-slider-label" className="visually-hidden">
                Distance - Number
              </span>
              <Slider
                value={distance}
                onChange={handleDistanceChange}
                className={styles.slider}
                min={sliderConfig.min}
                max={sliderConfig.max}
                size="medium"
                data-testid="sliderHandle"
                aria-labelledby="distance-slider-label"
              />
              <Box component="form" noValidate autoComplete="off" className={styles.distanceInput}>
                <label htmlFor="distance-number-input" className="visually-hidden">
                  Distance - Number
                </label>
                <FormControl>
                  <OutlinedInput
                    id="distance-number-input"
                    placeholder=""
                    size="small"
                    value={distance}
                    onChange={handleDistanceInputChange}
                    onBlur={handleDistanceBlur}
                    onKeyDown={distanceKeyDown}
                    data-testid="distanceInput"
                    aria-label="Distance input"
                  />
                </FormControl>
              </Box>
              <Box>
                <span id="metric-type-selection-label" className="visually-hidden">
                  Distance - Imperial or Metric
                </span>
                <FormControl>
                  <Select
                    id="metric-type-selection"
                    name="metricType"
                    value={metricType}
                    onChange={onChangeMetricType}
                    size="small"
                    className={styles.metricTypeIpunt}
                    data-testid="metricTypeInput"
                    aria-labelledby="metric-type-selection-label"
                  >
                    <MenuItem value="mi" className={styles.metricTypeOption}>
                      mi
                    </MenuItem>
                    <MenuItem value="km" className={styles.metricTypeOption}>
                      km
                    </MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </div>
          </fieldset>
            <div className={styles.footer}>
              <div>
                <ButtonFlatMedium data-testid="cancelButton" onClick={onClickButtonCancel}>
                  Cancel
                </ButtonFlatMedium>
              </div>
              <div>
                <ButtonPrimaryMedium
                  data-testid="saveButton"
                  onClick={onClickButtonSave}
                  disabled={isAddressInvalid}
                >
                  Save
                </ButtonPrimaryMedium>
              </div>
            </div>
          </div>
      </main>
    </>
  );
};
